import { AbilityKey } from '@percent/partner-dashboard/context/iam/abilityDefinitions'
import { PartnerScopes } from '@percent/partner-dashboard/constants/scopes'
import {
  POSTHOG_FEATURE_FLAGS,
  PostHogFeatureFlag
} from '@percent/partner-dashboard/common/hooks/usePostHogFeatureFlag/posthogFeatureFlags'

// Nav module groupings that can surface a "Reports" page (not backend domains).
export type ReportModule = 'donations' | 'workplace-giving' | 'grants' | 'verifications'

type TranslationKey = keyof typeof import('@percent/partner-dashboard/i18n/data/en/en.json')

// `domain`/`slug` address the report on the insights platform; `*Key`s are PD i18n keys.
export type ReportCard = {
  domain: string
  slug: string
  titleKey: TranslationKey
  descriptionKey: TranslationKey
  featureFlag?: PostHogFeatureFlag
}

// Frontend-owned catalogue of reports per module (pbp-api has no list endpoint yet).
const REPORTS_BY_MODULE: Record<ReportModule, ReportCard[]> = {
  donations: [],
  'workplace-giving': [
    {
      domain: 'giving',
      slug: 'employee-donations',
      titleKey: 'reports.donations.title',
      descriptionKey: 'reports.donations.description'
    }
  ],
  // Domains must match pbp-api's grants-insights register.ts.
  grants: [
    {
      domain: 'grant-candidates',
      slug: 'candidates',
      titleKey: 'reports.candidates.title',
      descriptionKey: 'reports.candidates.description'
    },
    {
      domain: 'grant-tasks',
      slug: 'tasks',
      titleKey: 'reports.tasks.title',
      descriptionKey: 'reports.tasks.description'
    },
    {
      domain: 'grant-impact',
      slug: 'impact',
      titleKey: 'reports.impact.title',
      descriptionKey: 'reports.impact.description',
      featureFlag: POSTHOG_FEATURE_FLAGS.PD_INSIGHTS_REPORTS_GRANT_IMPACT
    }
  ],
  verifications: []
}

export const getModuleReports = (module: ReportModule): ReportCard[] => REPORTS_BY_MODULE[module]

export const moduleHasReports = (module: ReportModule): boolean => REPORTS_BY_MODULE[module].length > 0

export const getReportBySlug = (module: ReportModule, slug: string): ReportCard | undefined =>
  REPORTS_BY_MODULE[module].find(report => report.slug === slug)

// The module's reports-list and report-detail routes, built from the module name.
// Centralised here (dependency-free) so the nav link, breadcrumb and redirect can
// share them without importing RoutePath — which would cycle with Routes.tsx.
// Constraint: a module's key must equal its URL segment (e.g. workplace-giving →
// /workplace-giving/reports). The route-path test pins this so a mismatch fails loudly.
export const reportsBasePath = (module: ReportModule): string => `/${module}/reports`
export const reportDetailPath = (module: ReportModule, slug: string): string => `/${module}/reports/${slug}`

// Gate for the insights reports surface: rollout flag + PartnerInsights scope + permission.
export const canViewReports = ({
  insightsReportsFeatureFlag,
  scopes,
  userCan
}: {
  insightsReportsFeatureFlag: boolean
  scopes: Record<string, boolean>
  userCan: (ability: AbilityKey) => boolean
}): boolean =>
  insightsReportsFeatureFlag && Boolean(scopes[PartnerScopes.PartnerInsights]) && userCan('viewPartnerInsights')
